import { HttpStatusCode } from 'axios';
import { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { ClipLoader } from 'react-spinners';
import { AccentButton, NeutralButton } from '../../../../../shared/Buttons';
import useApi from '../../../../../utils/AxiosWrapper';
import { useUser } from '../../../../../utils/UserContext';
import { useScreenView } from '../../../../../utils/ScreenViewContext';
import { HoursOfOperation } from '../../../../signup/vendors/VendorBusinessDetails';
import HoursOfOperationWidget from '../../../../signup/vendors/widgets/HoursOfOperationsWidget';
import { useJsApiLoader } from '@react-google-maps/api';
import { GOOGLE_MAPS_LOADER_OPTIONS } from '../../../../../utils/googleMapsLoader';
import dayjs from 'dayjs';
import {
  AddressComponents,
  formatAddressComponents,
} from '../../../../../utils/AddressUtils';
import {
  CreateVendorBusinessLocationRequest,
  DocumentUploadPresignedUrlsRequest,
  Entity,
  PresignedUrlObject,
  VendorBusinessLocationDocumentSubType,
  VendorType,
  VendorTypeDisplayNames,
} from '../../../../../types/apiTypes';
import { ImageSquare, Trash } from '@phosphor-icons/react';
import { getFileExtension } from '../../../../../utils/FileUtils';
import { VendorBusinessLocation } from '../../../../../types/dbTypes';
import { Select } from 'antd';

interface CreateVendorLocationProps {
  userType: string;
  currentPage: string;
  currentTab: string;
  setCurrentTab: (tab: string) => void;
  subTab: string;
  setSubTab: (tab: string) => void;
}

const defaultHoursOfOperation = {
  Monday: {
    isOpen: true,
    from: dayjs('09:00 AM', 'hh:mm A').format('hh:mm A'),
    to: dayjs('05:00 PM', 'hh:mm A').format('hh:mm A'),
  },
  Tuesday: {
    isOpen: true,
    from: dayjs('09:00 AM', 'hh:mm A').format('hh:mm A'),
    to: dayjs('05:00 PM', 'hh:mm A').format('hh:mm A'),
  },
  Wednesday: {
    isOpen: true,
    from: dayjs('09:00 AM', 'hh:mm A').format('hh:mm A'),
    to: dayjs('05:00 PM', 'hh:mm A').format('hh:mm A'),
  },
  Thursday: {
    isOpen: true,
    from: dayjs('09:00 AM', 'hh:mm A').format('hh:mm A'),
    to: dayjs('05:00 PM', 'hh:mm A').format('hh:mm A'),
  },
  Friday: {
    isOpen: true,
    from: dayjs('09:00 AM', 'hh:mm A').format('hh:mm A'),
    to: dayjs('05:00 PM', 'hh:mm A').format('hh:mm A'),
  },
  Saturday: { isOpen: false, from: '', to: '' },
  Sunday: { isOpen: false, from: '', to: '' },
};

interface CreateVendorLocationForm {
  locationName: string;
  businessEmail: string;
  businessPhone: string;
  businessAddress: string;
  websiteUrl: string;
  descriptionOfServices: string;
  primaryVendorType: string;
  secondaryVendorTypes: string[];
  primary_vendor_type: string;
  secondary_vendor_types: string[];
  hoursOfOperation: {
    Monday: HoursOfOperation;
    Tuesday: HoursOfOperation;
    Wednesday: HoursOfOperation;
    Thursday: HoursOfOperation;
    Friday: HoursOfOperation;
    Saturday: HoursOfOperation;
    Sunday: HoursOfOperation;
  };
  address_st_number?: string;
  address_st_name?: string;
  address_unit?: string;
  address_city?: string;
  address_state?: string;
  address_zip?: string;
}

function CreateVendorLocation({
  userType,
  currentPage,
  currentTab,
  setCurrentTab,
  subTab,
  setSubTab,
}: CreateVendorLocationProps) {
  const { callProtectedApi, callApi } = useApi();
  const [loading, setLoading] = useState(false);
  const { screenView } = useScreenView();
  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CreateVendorLocationForm>({
    defaultValues: {
      locationName: '',
      hoursOfOperation: defaultHoursOfOperation,
      businessEmail: '',
      businessPhone: '',
      businessAddress: '',
      websiteUrl: '',
      primary_vendor_type: '',
      secondary_vendor_types: [],
    },
  });
  const hoursOfOperation = watch('hoursOfOperation');
  const [addressFields, setAddressFields] = useState<AddressComponents>({});
  const { userRecord, userId } = useUser();
  const navigate = useNavigate();

  const { isLoaded } = useJsApiLoader(GOOGLE_MAPS_LOADER_OPTIONS);

  const [loadingImage, setLoadingImage] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [hover, setHover] = useState(false);
  const handleMouseEnter = () => setHover(true);
  const handleMouseLeave = () => setHover(false);

  const handleReplaceClick = () => {
    document.getElementById('fileInput')?.click();
  };

  const uploadImage = async (
    locationId: string,
    fileExtension: string,
    file: File
  ) => {
    try {
      setLoadingImage(true);
      const responseDocUrl = await callProtectedApi(
        `/documents/vendorBusinessLocation/${locationId}`,
        'post',
        {
          document_count: 1,
          document_sub_type: VendorBusinessLocationDocumentSubType.Images,
          file_extensions: [fileExtension],
        } as DocumentUploadPresignedUrlsRequest,
        'Error occurred while uploading your location image.'
      );
      const url: PresignedUrlObject[] =
        responseDocUrl?.data?.data?.document_urls;
      callApi(
        '',
        'put',
        file,
        'Error occurred while uploading your location image.',
        {
          'Content-Type': file?.type,
        },
        url[0]?.presignedUrl,
        5
      ).then((result: any) => {
        if (result?.status === HttpStatusCode.Ok) {
          toast.success('Business location created successfully.');
          navigate('/dashboard/locations');
        } else {
          throw new Error(
            `Failed to upload ${file?.name ?? 'file'}. HTTP Error ` +
              result?.status
          );
        }
      });
    } catch (error: any) {
      console.error('Error occurred uploading location image.');
    } finally {
      setLoadingImage(false);
      setLoading(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (
      file &&
      ['image/png', 'image/jpeg', 'image/heic', 'image/heif'].includes(
        file.type
      )
    ) {
      setUploadedFile(file);
    } else {
      alert('Please upload a valid image file (png, jpeg, heic, heif).');
    }
  };

  useEffect(() => {
    for (const key in addressFields) {
      if (addressFields.hasOwnProperty(key)) {
        setValue(
          key as keyof CreateVendorLocationForm,
          addressFields[key as keyof AddressComponents]
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressFields]);

  useEffect(() => {
    if (!window.google || !isLoaded) {
      // If Google object is not loaded, return early
      return;
    }
    const autocomplete = new window.google.maps.places.Autocomplete(
      document.getElementById('businessAddress') as HTMLInputElement,
      {
        types: ['geocode'], // Can be restricted to specific types of places
        componentRestrictions: { country: 'us' }, // Optional: restrict to a specific country
      }
    );

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (place && place.formatted_address) {
        setValue('businessAddress', place.formatted_address); // Use React Hook Form's setValue to update form
      }
      if (place && place.address_components) {
        const formattedAddress = formatAddressComponents(
          place.address_components
        );
        setAddressFields(formattedAddress);
      }
    });
  }, [setValue, isLoaded]);

  const createBusinessLocation = async (
    locationInfo: CreateVendorLocationForm,
    vendor_id: string
  ) => {
    setLoading(true);
    try {
      const response = await callProtectedApi(
        '/vendorBusinessLocations',
        'post',
        {
          vendor_id: vendor_id,
          location_name: locationInfo?.locationName,
          business_phone_number: locationInfo?.businessPhone,
          email: locationInfo?.businessEmail,
          website_url: locationInfo?.websiteUrl,
          address_st_number: addressFields?.address_st_number,
          address_st_name: addressFields?.address_st_name,
          address_unit: addressFields?.address_unit,
          address_city: addressFields?.address_city,
          address_state: addressFields?.address_state,
          address_zip: addressFields?.address_zip,
          hours_of_operation: JSON.stringify(locationInfo?.hoursOfOperation),
          description_of_services: locationInfo?.descriptionOfServices,
          primary_vendor_type: locationInfo?.primary_vendor_type,
          secondary_vendor_types: locationInfo?.secondary_vendor_types,
        } as CreateVendorBusinessLocationRequest,
        'Error occurred while creating your vendor business location.'
      );
      const data = response?.data?.data?.vendorBusinessLocation;
      if (
        data &&
        Object.entries(data)?.length > 0 &&
        (response?.status === HttpStatusCode.Ok ||
          response?.status === HttpStatusCode.Created)
      ) {
        return data as VendorBusinessLocation;
      }
    } catch (e: any) {
      console.error(
        'Error occurred while creating your new vendor business location: ',
        e
      );
    }
  };

  const onSubmit: SubmitHandler<CreateVendorLocationForm> = async (data) => {
    const location = await createBusinessLocation(data, userId ?? '');
    if (uploadedFile) {
      const fileExtension = getFileExtension(uploadedFile);
      if (fileExtension && uploadedFile) {
        if (
          userRecord?.userType === Entity.Vendor &&
          location &&
          location?.location_id &&
          location?.location_id?.length > 0
        ) {
          uploadImage(location?.location_id ?? '', fileExtension, uploadedFile);
        }
      }
    } else {
      if (location) {
        setLoading(false);
        toast.success('Business location created successfully.');
        navigate('/dashboard/locations');
      }
    }
  };
  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
      case 'tablet':
      case 'mobile':
        return (
          <div className="flex h-full w-full items-center justify-center overflow-y-auto p-4">
            <div className="flex h-full flex-col items-center justify-between space-y-[16px] bg-white sm:w-[95%] md:w-full lg:w-[1148px] lg:max-w-[1148px]">
              <form
                onSubmit={handleSubmit(onSubmit)}
                className={`flex h-full flex-col items-center space-y-[16px] overflow-x-hidden bg-white p-6 px-[10%] sm:w-full lg:w-[1148px] lg:max-w-[1148px]`}
              >
                <>
                  <label className="text-left text-[16px] text-graysecondary">
                    Location Image
                  </label>
                  <div
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    className="relative"
                  >
                    {!loadingImage ? (
                      <>
                        <div className="flex h-[200px] w-[200px] flex-col items-center justify-center rounded-[4px] border-[1px] border-[#EAECF0] align-middle">
                          <div className="flex h-[64px] w-[64px] flex-col items-center justify-center rounded-[4px] border-[1px] border-[#EAECF0] bg-[#F5F6F7] align-middle">
                            <ImageSquare
                              size={40}
                              className="flex items-center justify-center text-graysecondary"
                              weight="bold"
                            />
                          </div>
                        </div>
                      </>
                    ) : (
                      <ClipLoader
                        color="#1D213B"
                        loading={loadingImage}
                        size={40}
                        aria-label="Loading Spinner"
                        data-testid="loader"
                        className="flex h-full w-full justify-center"
                      />
                    )}
                    {hover && (
                      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black bg-opacity-50 text-white">
                        <p className="text-sm font-semibold">Replace Photo</p>
                        <div className="flex w-full items-center justify-center space-x-2">
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                            className="rounded-full bg-red-500 p-2"
                          >
                            <Trash size={24} className="text-white" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              handleReplaceClick();
                              e.stopPropagation();
                            }}
                            className="rounded-full bg-yellow-500 p-2"
                          >
                            Replace
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
                {uploadedFile && (
                  <h2 className="max-w-[200px] text-green-400">
                    Added {uploadedFile.name}
                  </h2>
                )}
                <input
                  type="file"
                  id="fileInput"
                  accept="image/png, image/jpeg, image/heic, image/heif"
                  style={{ display: 'none' }}
                  onChange={(e: any) => handleFileChange(e)}
                />
                <div className="flex w-full flex-col items-center gap-4 align-middle lg:flex-row">
                  <div className="w-full lg:w-[50%]">
                    <label
                      htmlFor="businessEmail"
                      className="block text-[16px] text-graysecondary"
                    >
                      Business Email
                    </label>
                    <input
                      type="text"
                      placeholder="info@truckservicedepot.com"
                      {...register('businessEmail', {
                        required: 'Business name is required',
                      })}
                      id="businessEmail"
                      className={`mt-1 block w-full border px-3 py-2 ${errors.businessEmail ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
                    />
                    {errors.businessEmail && (
                      <p className="mt-1 text-sm text-red-500">
                        {errors.businessEmail.message}
                      </p>
                    )}
                  </div>

                  <div className="w-full lg:w-[50%]">
                    <label
                      htmlFor="businessPhone"
                      className="block text-[16px] text-graysecondary"
                    >
                      Business Phone
                    </label>
                    <input
                      type="tel"
                      placeholder="8004360832"
                      {...register('businessPhone', {
                        required: 'Business phone number is required',
                        pattern: {
                          value: /^[0-9]{10}$/,
                          message: 'Invalid phone number, must be 10 digits',
                        },
                      })}
                      id="businessPhone"
                      className={`mt-1 block w-full border px-3 py-2 ${errors.businessPhone ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
                    />
                    {errors.businessPhone && (
                      <p className="mt-1 text-sm text-red-500">
                        {errors.businessPhone.message}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex w-full flex-col items-center gap-4 align-middle lg:flex-row">
                  <div className="w-full lg:w-[50%]">
                    <label
                      htmlFor="businessAddress"
                      className="block text-[16px] text-graysecondary"
                    >
                      First Location Business Address
                    </label>
                    <input
                      {...register('businessAddress', {
                        required: 'Business address is required',
                      })}
                      id="businessAddress"
                      className={`mt-1 block w-full border px-3 py-2 ${errors.businessAddress ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
                    />
                    {errors.businessAddress && (
                      <p className="mt-1 text-sm text-red-500">
                        {errors.businessAddress.message}
                      </p>
                    )}
                  </div>
                  <div className="w-full lg:w-[50%]">
                    <label
                      htmlFor="websiteUrl"
                      className="block text-[16px] text-graysecondary"
                    >
                      Website URL
                    </label>
                    <input
                      type="text"
                      placeholder="https://truckservicedepot.com"
                      {...register('websiteUrl', {
                        required: 'Website URL is required',
                      })}
                      id="websiteUrl"
                      className={`mt-1 block w-full border px-3 py-2 ${errors.websiteUrl ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
                    />
                    {errors.websiteUrl && (
                      <p className="mt-1 text-sm text-red-500">
                        {errors.websiteUrl.message}
                      </p>
                    )}
                  </div>
                </div>

                <div className="w-full">
                  <label
                    htmlFor="locationName"
                    className="block text-[16px] text-graysecondary"
                  >
                    Location Name
                  </label>
                  <input
                    type="text"
                    placeholder="Truck Service Depot - Atlanta"
                    {...register('locationName', {
                      required: 'Location name is required',
                    })}
                    id="locationName"
                    className={`mt-1 block w-full border px-3 py-2 ${errors.locationName ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
                  />
                  {errors.locationName && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors.locationName.message}
                    </p>
                  )}
                </div>

                <div className="flex w-full flex-row items-center gap-x-[16px] align-middle">
                  <div className="w-[50%]">
                    <label
                      htmlFor="primary_vendor_type"
                      className="block text-[16px] text-graysecondary"
                    >
                      Primary Service Offered
                    </label>
                    <Select
                      id="primary_vendor_type"
                      className="mt-1 w-full"
                      value={watch('primary_vendor_type')}
                      onChange={(value) =>
                        setValue('primary_vendor_type', value)
                      }
                      options={Object.values(VendorType).map((type) => ({
                        value: type,
                        label: VendorTypeDisplayNames[type],
                      }))}
                    />
                  </div>
                  <div className="w-[50%]">
                    <label
                      htmlFor="vendor_type"
                      className="block text-[16px] text-graysecondary"
                    >
                      Other Services Offered
                    </label>
                    <Select
                      id="secondary_vendor_types"
                      mode="multiple"
                      placeholder="Select service types"
                      className="mt-1 w-full"
                      value={watch('secondary_vendor_types')}
                      onChange={(value) =>
                        setValue('secondary_vendor_types', value)
                      }
                      options={Object.values(VendorType).map((type) => ({
                        value: type,
                        label: VendorTypeDisplayNames[type],
                      }))}
                    />
                  </div>
                </div>

                <HoursOfOperationWidget
                  control={control}
                  hoursOfOperation={hoursOfOperation}
                />

                <div className="w-full">
                  <label
                    htmlFor="descriptionOfServices"
                    className="block text-[16px] text-graysecondary"
                  >
                    Description of Services
                  </label>
                  <textarea
                    {...register('descriptionOfServices', {
                      required: 'Description of services is required',
                    })}
                    id="descriptionOfServices"
                    placeholder="Describe the services you offer"
                    className={`mt-1 block min-h-[150px] w-full border px-3 py-2 ${
                      errors.descriptionOfServices
                        ? 'border-red-500'
                        : 'border-gray-300'
                    } rounded-md shadow-sm focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
                  />
                  {errors.descriptionOfServices && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors.descriptionOfServices.message}
                    </p>
                  )}
                </div>
              </form>
              <div className="flex w-full items-center justify-center gap-4 border-t-[1px] border-[#EAECF0] align-middle sm:flex-col sm:py-4 md:h-[80px] md:max-h-[80px] md:min-h-[80px] md:flex-row md:px-4">
                <NeutralButton
                  onClick={() => navigate('/dashboard/locations')}
                  addClass="rounded-full w-[250px] h-[48px]"
                >
                  Cancel
                </NeutralButton>
                <AccentButton
                  addClass={`h-[48px] w-[250px] rounded-full`}
                  submit={true}
                  onClick={handleSubmit(onSubmit)}
                >
                  {!loading ? (
                    'Add Business Location'
                  ) : (
                    <ClipLoader
                      color="#1D213B"
                      loading={loading}
                      size={20}
                      aria-label="Loading Spinner"
                      data-testid="loader"
                      className="flex h-full w-full justify-center"
                    />
                  )}
                </AccentButton>
              </div>
            </div>
          </div>
        );
    }
  };

  return <>{renderScreenView(screenView)}</>;
}

export default CreateVendorLocation;
